import CancelIcon from '@precis-digital/kurama/icons/CancelIcon';
import SortAscendingIcon from '@precis-digital/kurama/icons/SortAscendingIcon';
import SortDescendingIcon from '@precis-digital/kurama/icons/SortDescendingIcon';
import { styled } from '@precis-digital/kurama/utils';
import Tooltip from '@precis-digital/kurama/Tooltip';
import type { SortDirection } from '@tanstack/react-table';
import { useCallback, useState, type ReactElement } from 'react';
import type { ChartCardTFunction, ChartCardTKeys } from '../../../types';
import { headerIconSize } from '../constants';
import type { ColumnSetSortFn } from '../types';
interface TableHeadCellSortButtonProps {
  sorted: SortDirection | false;
  fixedSort: boolean;
  defaultSortDirection: SortDirection;
  setSort: ColumnSetSortFn | undefined;
  columnId: string;
  t: ChartCardTFunction;
}
type ExtendedSortDirection = SortDirection | 'none';
const sortIconMap: Record<ExtendedSortDirection, typeof SortAscendingIcon | typeof CancelIcon> = {
  asc: SortAscendingIcon,
  desc: SortDescendingIcon,
  none: CancelIcon
};
const getClickSortDirection = (sorted: SortDirection | false, defaultSortDirection: SortDirection): ExtendedSortDirection => {
  if (sorted === false) {
    return defaultSortDirection;
  }
  if (sorted === defaultSortDirection) {
    return sorted === 'asc' ? 'desc' : 'asc';
  }
  return 'none';
};
const sortDirectionToTKey: Record<ExtendedSortDirection, ChartCardTKeys> = {
  asc: 'sortAscending',
  desc: 'sortDescending',
  none: 'sortNone'
};
const getSortIcon = (hovered: boolean, sorted: SortDirection | false, defaultSortDirection: SortDirection, clickSortDirection: ExtendedSortDirection): typeof SortAscendingIcon => {
  if (sorted === false) {
    return sortIconMap[defaultSortDirection];
  }
  if (hovered) {
    return sortIconMap[clickSortDirection];
  }
  return sortIconMap[sorted];
};
const TableHeadCellSortButton = ({
  sorted,
  fixedSort,
  defaultSortDirection,
  setSort,
  columnId,
  t
}: TableHeadCellSortButtonProps): ReactElement | null => {
  const clickSortDirection = getClickSortDirection(sorted, defaultSortDirection);
  const clickSortTooltip = t(sortDirectionToTKey[clickSortDirection]);
  const [hovered, setHovered] = useState(false);
  const SortIcon = getSortIcon(hovered, sorted, defaultSortDirection, clickSortDirection);
  const onClickHandler = useCallback(() => {
    setSort?.(columnId, clickSortDirection === 'none' ? false : clickSortDirection);
  }, [setSort, columnId, clickSortDirection]);
  const onMouseEnter = useCallback(() => {
    setHovered(true);
  }, []);
  const onMouseLeave = useCallback(() => {
    setHovered(false);
  }, []);
  return <Tooltip key="sort-button-tooltip" kind="singleline" title={clickSortTooltip}>
      <StyledSortIcon isSorted={sorted !== false && !hovered} onClick={fixedSort ? undefined : onClickHandler} onMouseEnter={fixedSort ? undefined : onMouseEnter} onMouseLeave={fixedSort ? undefined : onMouseLeave}>
        <SortIcon {...headerIconSize} />
      </StyledSortIcon>
    </Tooltip>;
};
const StyledSortIcon = styled('span', {
  shouldForwardProp: prop => prop !== 'isSorted'
})<{
  isSorted: boolean;
}>(({
  theme,
  isSorted
}) => ({
  color: isSorted ? theme.tokens.foreground.main.link : theme.tokens.foreground.main.secondary,
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer'
}));
export default TableHeadCellSortButton;